<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import tippy, { roundArrow } from 'tippy.js';
import 'tippy.js/dist/svg-arrow.css';
import { Editor, EditorContent } from '@tiptap/vue-3';
import { Bold } from '@tiptap/extension-bold';
import { BulletList } from '@tiptap/extension-bullet-list';
import { Document } from '@tiptap/extension-document';
import { Heading } from '@tiptap/extension-heading';
import { Italic } from '@tiptap/extension-italic';
import { ListItem } from '@tiptap/extension-list-item';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Text } from '@tiptap/extension-text';
import { Underline } from '@tiptap/extension-underline';

const props = defineProps<{
  structureLink: HTMLAnchorElement
}>();

let instanceTippy = null;

const editor = new Editor({
  content: '',
  extensions: [
    Bold, BulletList, Document, Heading, Italic, ListItem, Paragraph, Text, Underline
  ],
  editable: false
})

let preview = ref<{
  id: string;
  text: Element;
  image: {
    src: string | undefined;
    alt: string | undefined;
    background: string | undefined;
  };
} | null>(null);
const getDefinitionFromLink = async (link) => {
  if (preview.value !== null) {
    return;
  }

  const regex = /-(\d+)$/;
  const hash = regex.exec(new URL(link).pathname);

  const res = await fetch(`/api/structure/resume/${hash[1]}`);
  if (!res.ok) {
    throw new Error(`Broken link in description with hash : ${hash}`);
  }
  const resume = await res.json();

  const definitionContent = document.createElement('div')
  definitionContent.innerHTML = resume.text;

  return preview.value = {
    id: resume.link,
    text: definitionContent,
    image: {
      src: resume.imageSrc,
      alt: resume.imageAlt,
      background: resume.imageBackground,
    }
  }
}

const previewTrigger = ref(null);
const previewContent = ref(null);

const hasLoad = ref(false);
const isTrigger = ref(false);

const hasImage = computed(() => preview.value?.image?.src !== undefined);
const hasText = computed(() => preview.value?.text.childNodes.length >= 1);
const imgBgColor = computed(() => preview.value?.image.background);
const definitionImagePreview = computed(() => preview.value?.image);

onMounted(() => {
  instanceTippy = tippy(previewTrigger.value, {
    content: previewContent.value,
    placement: 'top',
    theme: 'preview-tooltip',
    maxWidth: 480,
    delay: [ 300, null ],
    interactive: true,
    offset: [ 0, 6 ],
    aria: {
      content: 'labelledby',
    },
    appendTo: document.body,
    async onShow(instance) {
      if (hasLoad.value) {
        return;
      }

      await getDefinitionFromLink(props.structureLink.href);

      if (!isTrigger.value) {
        return;
      }

      instance.disable();

      editor.chain()
        .setContent(preview.value?.text?.innerHTML, false)
        .run();

      hasLoad.value = true;

      if (hasImage.value || hasText.value) {
        instance.setProps({
          arrow: roundArrow,
        });
      }

      instance.enable();
      instance.show();
    },
    onTrigger(instance, event) {
      isTrigger.value = true;
      instance.setProps({
        getReferenceClientRect: () => ({
          width: 0,
          height: 0,
          top: event.target.getBoundingClientRect().top,
          bottom: event.target.getBoundingClientRect().bottom,
          left: event.clientX,
          right: event.clientX,
        }),
      });
    },
    onUntrigger(instance, event) {
      isTrigger.value = false;
    }
  });
});

onUnmounted(() => {
  instanceTippy.destroy();
})

const linkClass = props.structureLink.className;
</script>

<template>
  <span>
    <a
      ref="previewTrigger"
      target="_blank"
      :href="structureLink.href"
      v-html="structureLink.innerHTML"
      :class="linkClass"
    />

    <a
      :href="structureLink.href"
      ref="previewContent"
      :class="{'text-only': !hasImage && hasText}"
      class="preview-container"
    >

      <editor-content
        v-if="hasText"
        class="preview-text"
        :editor="editor"
      />

      <img
        v-if="hasImage"
        :src="definitionImagePreview.src"
        :alt="definitionImagePreview.alt"
        class="preview-img"
        :class="{'img-only': hasImage && !hasText}"
        :style="{backgroundColor: `#${imgBgColor}`}"
        width="180" height="180"
      />
    </a>
  </span>
</template>

<style lang="scss">
$tooltip-width: 480px;
$tooltip-height: 220px;
$image-only-width: 250px;

.tippy-box[data-theme~='preview-tooltip'] {
  background-color: var(--layer-02);
  color: var(--text-primary);
  border-radius: 0.25rem;
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, var(--button-opacity-hover));
  line-height: inherit;
  font-size: inherit;

  > .tippy-arrow:before {
    content: none;
  }


  &[data-placement='bottom'] {
    transform: translate3d(0px, 20px, 0px);
  }

  .tippy-content {
    padding: 0;

    ul {
      padding-left: 1rem;
      margin-bottom: 0;

      li {
        list-style: disc;
      }
    }

    p {
      margin: 0;
    }
  }

  .tippy-svg-arrow {
    fill: var(--layer-02);
  }
}

.preview-container {
  display: flex;
  color: inherit !important;
  text-decoration: none !important;

  &.text-only {
    width: $tooltip-width;
    height: $tooltip-height;
  }

  .preview-text {
    margin: 1rem;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 9;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    min-width: calc($tooltip-width - $tooltip-height - 32px);

    li {
      margin-left: 5px;
      margin-bottom: unset;
    }
  }

  .preview-img {
    object-fit: contain;
    aspect-ratio: 1 / 1;
    flex-shrink: 0;
    width: $tooltip-height;
    height: $tooltip-height;
    border-radius: 0 0.25rem 0.25rem 0;
    border-left: 2px solid var(--com-divider-border-primary);

    &.img-only {
      width: $image-only-width;
      height: $image-only-width;
      border-radius: 0.25rem;
      border-left: none;
    }
  }
}
</style>
